import { useEffect, useMemo } from 'react';
import { Modal, Form, Select, Checkbox, Tag, Alert, Button, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';

interface Props {
  open: boolean;
  onClose: () => void;
  customerType: 'RETAILER' | 'FARMER';
  /** The retailer/farmer being moved — needs at least id, territoryId and a display name. */
  customer: any;
}

/**
 * Move a retailer/farmer to a different territory. The owner recomputes to the new
 * territory's primary SP unless "keep current owner" is ticked. Territory choices are
 * limited to the actor's area (full-access roles get all) by the backend.
 */
export function ChangeTerritoryModal({ open, onClose, customerType, customer }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const name = customer?.companyName ?? customer?.fullName ?? 'customer';

  const { data: territories } = useQuery({
    queryKey: ['my-territories'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
    enabled: open,
  });

  const terrMap = useMemo(() => new Map((territories ?? []).map((t: any) => [String(t.id), t])), [territories]);
  const currentLabel = (() => {
    const t: any = terrMap.get(String(customer?.territoryId));
    return t ? `${t.code} · ${t.name}` : (customer?.territoryId ? `#${customer.territoryId}` : '—');
  })();

  useEffect(() => {
    if (open) form.setFieldsValue({ territoryId: undefined, keepOwner: false });
  }, [open, form]);

  const save = useMutation({
    mutationFn: (vals: any) => request({
      url: '/territory-assignments/reassign-customer', method: 'POST',
      data: { customerType, customerId: String(customer.id), territoryId: vals.territoryId, keepOwner: !!vals.keepOwner },
    }),
    onSuccess: () => {
      message.success('Territory changed');
      qc.invalidateQueries({ queryKey: ['retailers'] });
      qc.invalidateQueries({ queryKey: ['farmers'] });
      qc.invalidateQueries({ queryKey: ['retailer', String(customer.id)] });
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to change territory'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Change territory · ${name}`}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Change territory</Button></>}
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <div style={{ marginBottom: 14 }}>Current: <Tag>{currentLabel}</Tag></div>
        <Form.Item name="territoryId" label="New territory" rules={[{ required: true, message: 'Pick the new territory' }]}>
          <Select
            placeholder="Select territory"
            options={(territories ?? []).map((t: any) => ({ value: String(t.id), label: `${t.code} · ${t.name}` }))}
          />
        </Form.Item>
        <Form.Item name="keepOwner" valuePropName="checked"
          extra="By default the owner becomes the new territory's primary Sales Person.">
          <Checkbox label="Keep the current owner (don't reassign)" />
        </Form.Item>
        <Alert type="info"
          title="Open orders, invoices and outstanding stay with the customer — only the territory (and owner) change." />
      </Form>
    </Modal>
  );
}
